import { lazy, Suspense } from "react";
import Loading from "../components/commonControls/loading";
import {
  signUpRoute,
  loginRoute,
  pushNotificationRoute,
  createUserRoute,
  modifyRolesRoute,
  usersRoute,
  rolesRoute,
  centersRoute,
  createCenterRoute,
  editCenterRoute,
  deleteCenterRoute,
  userGroupsRoute,
  createGroupRoute,
  groupAddRoute,
  editProfile,
  changePassword,
  dashboardRoute,
  forgotPasswordRoute,
  OTPVerifyRoute,
  resetPasswordRoute,
  createNotificationsRoute,
  // Fixafib Routes
  patientsRoute,
  tasksRoute,
  reportsRoute,
  caseDaysRoute,
  registryRoute,
  patientDetailRoute,
  createPatientRoute,
  contactsRoute,
  insurancesRoute,
  devicesRoute,
  indicationsRoute,
  specialtiesRoute,
  medicationsRoute,
  medicationFrequenciesRoute,
  medicationHoldTimingsRoute,
  centerLocationsRoute,
  complicationsRoute,
  messagesRoute,
  messageTemplatesRoute,
  communicationMessageTemplatesRoute,
  communicationEmailTemplatesRoute,
  contactDropdownFiltersRoute,
  callsRoute,
  inboxRoute,
  communicationModesRoute,
  termsAndConditions,
  termsOfServiceRoute,
  privacyPolicyRoute,
  preferences,
  subjectAccessRequestRoute,
  subjectAccessRequestsManagementRoute,
  sarRequestsRoute,
} from "./reactRoutes";
import AuthRoute from "../middlewares/AuthRoute";

//Import components for routing that don't need to wait for data from api before loading
const Login = lazy(() => import("../components/authentication/Login"));
const RegisterSimple = lazy(() => import("../components/authentication/RegisterSimple"));
const ForgetPassword = lazy(() => import("../components/authentication/ForgetPassword"));
const OTPVerify = lazy(() => import("../components/authentication/OTPVerify"));
const ResetPassword = lazy(() => import("../components/authentication/ResetPassword"));
const TermsOfServicePage = lazy(() => import("../components/legal/TermsOfServicePage"));
const PrivacyPolicyPage = lazy(() => import("../components/legal/PrivacyPolicyPage"));
const SubjectAccessRequestPage = lazy(() =>
  import("../components/subjectAccessRequests/SubjectAccessRequestPage")
);

//title suffix
const suffix = " - Fixafib";

// Fixafib Components
const Patients = lazy(() => import("../components/patients/Patients"));
const Tasks = lazy(() => import("../components/tasks/Tasks"));
const Reports = lazy(() => import("../components/reports/Reports"));
const CaseDays = lazy(() => import("../components/case-days/CaseDays"));
const Registry = lazy(() => import("../components/registry/Registry"));
const PatientDetail = lazy(() => import("../components/patients/PatientDetail"));
const CreatePatient = lazy(() => import("../components/patients/CreatePatient"));
const InsurancesManagement = lazy(() => import("../components/insurances/InsurancesManagement"));
const DeviceManagement = lazy(() => import("../components/devices/DeviceManagement"));
const IndicationsManagement = lazy(() => import("../components/indications/IndicationsManagement"));
const SpecialtiesManagement = lazy(() => import("../components/specialties/SpecialtiesManagement"));
const MedicationsManagement = lazy(() => import("../components/medications/MedicationsManagement"));
const MedicationFrequenciesManagement = lazy(() => import("../components/medicationFrequencies/MedicationFrequenciesManagement"));
const MedicationHoldTimingsManagement = lazy(() => import("../components/medicationHoldTimings/MedicationHoldTimingsManagement"));
const CenterLocationsManagement = lazy(() => import("../components/centerLocations/CenterLocationsManagement"));
const ComplicationsManagement = lazy(() => import("../components/complications/ComplicationsManagement"));
const MessagesManagement = lazy(() => import("../components/messages/MessagesManagement"));
const CallsManagement = lazy(() => import("../components/messages/CallsManagement"));
const Inbox = lazy(() => import("../components/inbox/Inbox"));
const MessageTemplatesManagement = lazy(() => import("../components/messages/MessageTemplatesManagement"));
const CommunicationMessageTemplates = lazy(() =>
  import("../components/communication/CommunicationMessageTemplates")
); // thin wrapper: MessageTemplatesManagement variant="communication"
const CommunicationEmailTemplates = lazy(() =>
  import("../components/communication/CommunicationEmailTemplates")
);
const CommunicationModes = lazy(() => import("../components/communication/CommunicationModes"));
const ContactDropdownFiltersManagement = lazy(() =>
  import("../components/contacts/ContactDropdownFiltersManagement")
);
const SubjectAccessRequestsManagement = lazy(() =>
  import("../components/subjectAccessRequests/SubjectAccessRequestsManagement")
);
const SubjectAccessRequestsAssigned = lazy(() =>
  import("../components/subjectAccessRequests/SubjectAccessRequestsAssigned")
);
const TermsAndConditions = lazy(() => import("../components/Profile/TermsAndConditions"));
const Preferences = lazy(() => import("../components/Profile/Preferences"));
// Lazy Import other components for routes
const Push = lazy(() => import("../components/pushMessages/Push"));
const Notifications = lazy(() =>
  import("../components/pushMessages/Notifications")
);
const RoleEdit = lazy(() => import("../components/usersRoles/RoleEdit"));
const UserCreate = lazy(() => import("../components/usersRoles/UserCreate"));
const RoleManagement = lazy(() =>
  import("../components/usersRoles/RoleManagement")
);
const UserManagement = lazy(() =>
  import("../components/usersRoles/UserManagement")
);
const CenterManagement = lazy(() =>
  import("../components/centers/CenterManagement")
);
const ContactsManagement = lazy(() => import("../components/contacts/ContactsManagement"));
const CenterCreate = lazy(() =>
  import("../components/centers/CenterCreate")
);
const EditCenter = lazy(() => import("../components/centers/EditCenter"));
const DeleteCenter = lazy(() => import("../components/centers/DeleteCenter"));

const GroupManagement = lazy(() =>
  import("../components/groups/GroupManagement")
);
const CreateGroup = lazy(() => import("../components/groups/GroupCreate"));
const GroupAdd = lazy(() => import("../components/groups/GroupAdd"));
const EditProfile = lazy(() => import("../components/Profile/EditProfile"));
const ChangePassword = lazy(() =>
  import("../components/Profile/ChangePassword")
);

const Dashboard = lazy(() => import("../components/dashboard/Dashboard"));

// List of all private Routes with corresponding components
export const PrivateRoutes = [
  {
    path: editProfile,
    element: (
      <Suspense fallback={<Loading />}>
        <EditProfile title={`Update Profile${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: changePassword,
    element: (
      <Suspense fallback={<Loading />}>
        <ChangePassword title={`Change Password${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: usersRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <UserManagement title={`Users${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: termsAndConditions,
    element: (
      <Suspense fallback={<Loading />}>
        <TermsAndConditions title={`Terms and Conditions${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: preferences,
    element: (
      <Suspense fallback={<Loading />}>
        <Preferences title={`Preferences${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: centersRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <CenterManagement title={`Centers${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: contactsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <ContactsManagement title={`Contacts${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: createCenterRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <CenterCreate title={`Create Center${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: editCenterRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <EditCenter title={`Edit Center${suffix}`} />
      </Suspense>
    ),
  },
  // {
  //   path: deleteCenterRoute,
  // },
  {
    path: deleteCenterRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <DeleteCenter title={`Delete Center${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: rolesRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <RoleManagement title={`Roles${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: modifyRolesRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <RoleEdit title={`Update Roles${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: createUserRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <UserCreate title={`Register User${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: createNotificationsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <Push title={`Create Notifications${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: pushNotificationRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <Notifications title={`Notifications${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: userGroupsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <GroupManagement title={`Groups${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: createGroupRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <CreateGroup title={`Update Groups${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: groupAddRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <GroupAdd title={`Update Group Members${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: dashboardRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <Dashboard title={`Home${suffix}`} />
      </Suspense>
    ),
  },
  // Fixafib Routes
  {
    path: patientsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <Patients title={`Patients${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: tasksRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <Tasks title={`Tasks${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: reportsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <Reports title={`Reports${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: caseDaysRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <CaseDays title={`Procedure Days${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: registryRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <Registry title={`Registry${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: patientDetailRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <PatientDetail title={`Patient Details${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: createPatientRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <CreatePatient title={`Create Patient${suffix}`} />
      </Suspense>
    ),
  },
  // Super Admin Routes
  {
    path: insurancesRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <InsurancesManagement title={`Insurances${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: devicesRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <DeviceManagement title={`Devices${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: indicationsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <IndicationsManagement title={`Indications${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: specialtiesRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <SpecialtiesManagement title={`Specialties${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: medicationsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <MedicationsManagement title={`Medications${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: medicationFrequenciesRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <MedicationFrequenciesManagement title={`Medication Frequencies${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: medicationHoldTimingsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <MedicationHoldTimingsManagement />
      </Suspense>
    ),
  },
  {
    path: centerLocationsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <CenterLocationsManagement />
      </Suspense>
    ),
  },
  {
    path: complicationsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <ComplicationsManagement title={`Complications${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: subjectAccessRequestsManagementRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <SubjectAccessRequestsManagement title={`Subject Access Requests${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: sarRequestsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <SubjectAccessRequestsAssigned title={`SAR Requests${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: inboxRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <Inbox />
      </Suspense>
    ),
  },
  {
    path: messagesRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <MessagesManagement title={`Messages${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: callsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <CallsManagement title={`Calls${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: messageTemplatesRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <MessageTemplatesManagement title={`Message Templates${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: communicationMessageTemplatesRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <CommunicationMessageTemplates title={`Message Templates (Communication)${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: communicationEmailTemplatesRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <CommunicationEmailTemplates title={`Email Templates${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: communicationModesRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <CommunicationModes />
      </Suspense>
    ),
  },
  {
    path: contactDropdownFiltersRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <ContactDropdownFiltersManagement title={`Contact Dropdown Filters${suffix}`} />
      </Suspense>
    ),
  },
];

// List of all public routes with corresponding components
export const PublicRoutes = [
  {
    path: loginRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <AuthRoute>
          <Login title={`Login${suffix}`} />
        </AuthRoute>
      </Suspense>
    )
  },
  {
    path: signUpRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <AuthRoute>
          <RegisterSimple title={`Register${suffix}`} />
        </AuthRoute>
      </Suspense>
    ),
  },
  {
    path: termsOfServiceRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <TermsOfServicePage title={`Terms of Service${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: privacyPolicyRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <PrivacyPolicyPage title={`Privacy Policy${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: subjectAccessRequestRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <SubjectAccessRequestPage title={`Privacy Request${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: forgotPasswordRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <AuthRoute>
          <ForgetPassword title={`Password Reset${suffix}`} />
        </AuthRoute>
      </Suspense>
    ),
  },
  {
    path: OTPVerifyRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <AuthRoute>
          <OTPVerify title={`Verify OTP${suffix}`} />
        </AuthRoute>
      </Suspense>
    ),
  },
  {
    path: resetPasswordRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <AuthRoute>
          <ResetPassword title={`Reset Password${suffix}`} />
        </AuthRoute>
      </Suspense>
    ),
  },
];
